import { useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { canAccessCheckout } from '@/lib/intentDetection';

/**
 * Route guard for checkout / upgrade pages.
 *
 * Rule (project): "Do not show checkout before answer + questionnaire flow."
 *
 * Allows access when ANY of:
 *   - the user has already paid (so they can manage / upgrade)
 *   - the gate is satisfied: an answer has been seen AND the questionnaire
 *     has been started or completed.
 *
 * Otherwise redirects to "/" (the intro / Ask flow) with a brief toast.
 */
export function CheckoutGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading: authLoading } = useAuth();
  const { hasPaidAccess, loading: subLoading } = useSubscription();

  useEffect(() => {
    if (authLoading || subLoading) return;
    if (hasPaidAccess) return; // paid users always allowed
    if (canAccessCheckout()) return;

    navigate('/', { replace: true, state: { from: location.pathname } });
  }, [authLoading, subLoading, hasPaidAccess, navigate, location.pathname]);

  // Render children optimistically; the redirect above runs once gating data is ready.
  return <>{children}</>;
}
